import {useState, useEffect} from 'react';
import {Link, useLocation} from 'react-router-dom';

import {useFavorites} from '../../hooks/useFavorites';
import {usePages} from '../../context/PagesContext';

import imgLogo from '/src/assets/img/logo.png';

import styles from "./Header.module.scss";

interface HeaderProps {
    scrollToFormRef: React.MutableRefObject<HTMLDivElement | null>;
}

function Header({scrollToFormRef}: HeaderProps) {
    const navLinks = [
        {path: "/", label: "Главная"},
        {path: "/services", label: "Услуги"},
        {path: "/projects", label: "Кейсы"},
        {path: "/process", label: "Процесс"},
        {path: "/reviews", label: "Отзывы"},
        {path: "/contacts", label: "Контакты"},
        {path: "/about", label: "O компании"},
    ];

    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const {refreshFavorites} = useFavorites();
    const {data} = usePages();

    const displayedPhone = data?.phone ?? '+7 (999) 999-99-99';
    const displayedAddress = data?.address ?? 'Нижний Новгород, Кремль, 15A';
    const isHomePage = ["/", "/admin", "/admin/"].includes(location.pathname);

    const handleNavClick = () => {
        window.scrollTo({top: 0, behavior: 'smooth'});
        setIsMobileMenuOpen(false);
    };

    const isActiveLink = (path: string) => {
        if (path === '/') return location.pathname === '/' || location.pathname === '/admin';
        return location.pathname.startsWith(path);
    };

    const formatTelHref = (phoneStr: string | null) => {
        if (!phoneStr) return '#callback';
        const sanitized = phoneStr.replace(/[^+\d]/g, '');
        return `tel:${sanitized}`;
    };

    const handleScrollToForm = () => {
        if (scrollToFormRef.current) {
            const element = scrollToFormRef.current;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({top: elementPosition, behavior: 'smooth'});
        }
    };

    const handleMobileScrollToForm = () => {
        setIsMobileMenuOpen(false);
        setTimeout(() => {
            if (scrollToFormRef.current) {
                const element = scrollToFormRef.current;
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - 100;
                window.scrollTo({top: offsetPosition, behavior: 'smooth'});
            }
        }, 300);
    };

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 250);
        window.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [isMobileMenuOpen]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1024 && isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isMobileMenuOpen]);

    useEffect(() => {
        refreshFavorites();
    }, [location.pathname, refreshFavorites]);

    return (
        <>
            <header
                className={`${styles.header} ${isScrolled ? styles.scrolled : ''} ${isMobileMenuOpen ? styles.menuOpen : ''} ${isHomePage ? styles.homePage : ''}`}>
                <div className={styles.container}>
                    <div className={`${styles.topbar} ${isScrolled ? styles.topbarHidden : ''}`}>
                        <div className={styles.contactInfo}>
                            <svg width="14" height="18" viewBox="0 0 14 18" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M9.5 6.875C9.5 7.53804 9.23661 8.17393 8.76777 8.64277C8.29893 9.11161 7.66304 9.375 7 9.375C6.33696 9.375 5.70107 9.11161 5.23223 8.64277C4.76339 8.17393 4.5 7.53804 4.5 6.875C4.5 6.21196 4.76339 5.57607 5.23223 5.10723C5.70107 4.63839 6.33696 4.375 7 4.375C7.66304 4.375 8.29893 4.63839 8.76777 5.10723C9.23661 5.57607 9.5 6.21196 9.5 6.875Z"
                                    stroke="#F59E0B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                                <path
                                    d="M13.25 6.875C13.25 12.8267 7 16.25 7 16.25C7 16.25 0.75 12.8267 0.75 6.875C0.75 5.2174 1.40848 3.62768 2.58058 2.45558C3.75268 1.28348 5.3424 0.625 7 0.625C8.6576 0.625 10.2473 1.28348 11.4194 2.45558C12.5915 3.62768 13.25 5.2174 13.25 6.875Z"
                                    stroke="#F59E0B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <p>{displayedAddress}</p>
                        </div>
                        <div className={styles.phoneSection}>
                            <a className={styles.phoneNumber}>
                                {displayedPhone}
                            </a>
                            <a href={formatTelHref(displayedPhone)} className={styles.callback}>
                                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1.03125 3.09375C1.03125 6.89058 4.10942 9.96875 7.90625 9.96875H8.9375C9.211 9.96875 9.47331 9.8601 9.6667 9.6667C9.8601 9.47331 9.96875 9.211 9.96875 8.9375V8.30867C9.96875 8.07217 9.80788 7.86592 9.57825 7.80863L7.55104 7.30171C7.34938 7.25129 7.13763 7.32692 7.01342 7.49283L6.56883 8.08546C6.43958 8.25779 6.21638 8.33388 6.01425 8.25963C5.2639 7.98377 4.58249 7.5481 4.01719 6.98281C3.4519 6.41751 3.01623 5.7361 2.74038 4.98575C2.66613 4.78363 2.74221 4.56042 2.91454 4.43117L3.50717 3.98658C3.67354 3.86238 3.74871 3.65017 3.69829 3.44896L3.19137 1.42175C3.16348 1.31024 3.09912 1.21124 3.00852 1.14049C2.91793 1.06974 2.80628 1.03129 2.69133 1.03125H2.0625C1.789 1.03125 1.52669 1.1399 1.3333 1.3333C1.1399 1.52669 1.03125 1.789 1.03125 2.0625V3.09375Z"
                                        stroke="#F0F0F0" strokeWidth="0.666667" strokeLinecap="round"
                                        strokeLinejoin="round"/>
                                </svg>
                                <span>Заказать звонок</span>
                            </a>
                        </div>
                    </div>

                    <div className={styles.bottombar}>
                        <Link to="/">
                            <div className={styles.logo}>
                                <img src={imgLogo} alt="СТВ Дизайн"/>
                            </div>
                        </Link>
                        <nav className={styles.navigation}>
                            <ul>
                                {navLinks.map(link => (
                                    <li className={isActiveLink(link.path) ? styles.active : ''} key={link.path}>
                                        <Link to={link.path} onClick={handleNavClick}>{link.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>

                        <div className={styles.application}>
                            {/*<Link to="/favorites" className={styles.favoritesLink}>*/}
                            {/*    <div className={styles.favoritesIcon}>*/}
                            {/*        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">*/}
                            {/*            <path*/}
                            {/*                d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z"*/}
                            {/*                stroke="#F59E0B"*/}
                            {/*                strokeWidth="1.5"*/}
                            {/*                fill="transparent"*/}
                            {/*            />*/}
                            {/*        </svg>*/}
                            {/*        {favoritesCount > 0 && (*/}
                            {/*            <span className={styles.favoritesBadge}>{favoritesCount}</span>*/}
                            {/*        )}*/}
                            {/*    </div>*/}
                            {/*</Link>*/}
                            <button
                                className={styles.applyBtn}
                                onClick={handleScrollToForm}
                            >
                                Оставить заявку
                            </button>
                        </div>
                        <button
                            className={`${styles.burger} ${isMobileMenuOpen ? styles.burgerActive : ''}`}
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-label="Открыть меню"
                        >
                            <span></span>
                            <span></span>
                            <span></span>
                        </button>
                    </div>
                </div>
            </header>

            <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuActive : ''}`}>
                <button
                    className={styles.closeButton}
                    onClick={() => setIsMobileMenuOpen(false)}
                    aria-label="Закрыть меню"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round"
                              strokeLinejoin="round"/>
                        <path d="M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round"
                              strokeLinejoin="round"/>
                    </svg>
                </button>

                <nav className={styles.mobileNav}>
                    <ul>
                        {navLinks.map(link => (
                            <li className={isActiveLink(link.path) ? styles.active : ''} key={link.path}>
                                <Link to={link.path} onClick={handleNavClick}>{link.label}</Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className={styles.mobileContacts}>
                    <div className={styles.mobilePhone}>
                        <a href={formatTelHref(displayedPhone)} className={styles.mobilePhoneNumber}>
                            {displayedPhone}
                        </a>
                        <button
                            className={styles.mobileCallback}
                            onClick={handleMobileScrollToForm}
                        >
                            Оставить заявку
                        </button>
                    </div>
                    <div className={styles.mobileAddress}>
                        <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M9.5 6.875C9.5 7.53804 9.23661 8.17393 8.76777 8.64277C8.29893 9.11161 7.66304 9.375 7 9.375C6.33696 9.375 5.70107 9.11161 5.23223 8.64277C4.76339 8.17393 4.5 7.53804 4.5 6.875C4.5 6.21196 4.76339 5.57607 5.23223 5.10723C5.70107 4.63839 6.33696 4.375 7 4.375C7.66304 4.375 8.29893 4.63839 8.76777 5.10723C9.23661 5.57607 9.5 6.21196 9.5 6.875Z"
                                stroke="#F59E0B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                            <path
                                d="M13.25 6.875C13.25 12.8267 7 16.25 7 16.25C7 16.25 0.75 12.8267 0.75 6.875C0.75 5.2174 1.40848 3.62768 2.58058 2.45558C3.75268 1.28348 5.3424 0.625 7 0.625C8.6576 0.625 10.2473 1.28348 11.4194 2.45558C12.5915 3.62768 13.25 5.2174 13.25 6.875Z"
                                stroke="#F59E0B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p>{displayedAddress}</p>
                    </div>
                </div>
            </div>

            <div
                className={`${styles.overlay} ${isMobileMenuOpen ? styles.overlayActive : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
            />
        </>
    );
}

export default Header;